import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Room } from '../types';
import { deletePhoto } from './photoService';
import { deleteItem } from './itemService';

// Collection reference
const roomsCollection = collection(db, 'rooms');

// Get a single room by ID
export const getRoom = async (roomId: string): Promise<Room> => {
  const roomDoc = await getDoc(doc(db, 'rooms', roomId));
  
  if (!roomDoc.exists()) {
    throw new Error(`Room with ID ${roomId} not found`);
  }
  
  return {
    id: roomDoc.id,
    ...roomDoc.data()
  } as Room;
};

// Get all rooms for a project
export const getRoomsByProject = async (projectId: string): Promise<Room[]> => {
  const roomsQuery = query(
    roomsCollection,
    where('projectId', '==', projectId),
    orderBy('name', 'asc')
  );
  
  const roomsSnapshot = await getDocs(roomsQuery);
  return roomsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Room));
};

// Create a new room
export const createRoom = async (roomData: Omit<Room, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ id: string }> => {
  const timestamp = Timestamp.now();
  
  const newRoom = {
    ...roomData,
    createdAt: timestamp,
    updatedAt: timestamp
  };
  
  const docRef = await addDoc(roomsCollection, newRoom);
  return { id: docRef.id };
};

// Update an existing room
export const updateRoom = async (
  roomId: string,
  roomData: Partial<Omit<Room, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<{ id: string }> => {
  const roomRef = doc(db, 'rooms', roomId);
  
  await updateDoc(roomRef, {
    ...roomData,
    updatedAt: Timestamp.now()
  });
  
  return { id: roomId };
};

// Delete a room and all associated items (with their photos) + room photos
export const deleteRoom = async (roomId: string, projectId?: string): Promise<void> => {
  if (projectId) {
    // Delete items belonging to this room (each item cascades to its photos)
    const itemsQuery = query(
      collection(db, 'items'),
      where('projectId', '==', projectId),
      where('roomId', '==', roomId)
    );
    const itemsSnap = await getDocs(itemsQuery);
    const itemResults = await Promise.allSettled(
      itemsSnap.docs.map(d => deleteItem(d.id, projectId))
    );

    // Delete photos directly attached to the room (not via items)
    const photosQuery = query(
      collection(db, 'photos'),
      where('projectId', '==', projectId),
      where('roomId', '==', roomId)
    );
    const photosSnap = await getDocs(photosQuery);
    const photoResults = await Promise.allSettled(
      photosSnap.docs.map(d => deletePhoto(d.id, projectId))
    );

    // Don't delete the room while children failed to delete — that would orphan
    // items/photos with a dangling roomId. Surface the failure so the caller can
    // retry instead of silently leaving inconsistent data.
    const failed = [...itemResults, ...photoResults].filter(r => r.status === 'rejected');
    if (failed.length > 0) {
      throw new Error(`Failed to delete ${failed.length} child record(s) in room ${roomId}; room not deleted`);
    }
  }

  await deleteDoc(doc(db, 'rooms', roomId));
}; 